'use client';

import { useCallback, useEffect, useState, type FC } from 'react';
import { observer, useObserve } from '@legendapp/state/react';
import { CartCounterButtonWidget } from './cart-counter-button-widget';
import type { CartCounterButtonProps } from './cart-counter-button.types';
import { useProductGiftHook } from '../../hooks/product-gift.hook';
import { cartItemReferenceStoreAPI, useAttachedCartItemReferenceLogics } from '@kidslabs/module-quote';
import styles from './cart-counter-button.module.css';

/**
 * CartCounterButton - Counter button with gift promotion awareness
 * Integrates with draft cart store to manage quantity stepper
 * When quantity changes, clears gift selection to force re-selection
 */
export const CartCounterButton: FC<CartCounterButtonProps> = observer(({ showIf, variant, product }) => {
    const quantityStepper$ = cartItemReferenceStoreAPI.state.getQuantityStepperObservable();
    const setQuantityStepper = cartItemReferenceStoreAPI.actions.setQuantityStepper;
    const [counterValue, setCounterValue] = useState(1);
    const { clearAttachedCartItemReferenceLogics } = useAttachedCartItemReferenceLogics();

    // Observe store for reactive updates when gifts are loaded from main component
    const { sortedGiftsDataList } = useProductGiftHook({
        product: product ?? undefined,
        observeStore: true,
    });

    const handleIncrease = useCallback((value: number) => {
        setCounterValue(prevValue => prevValue + value);
    }, []);

    const handleDecrease = useCallback(() => {
        setCounterValue(prevValue => (prevValue > 1 ? prevValue - 1 : 1));
    }, []);

    // Save to stepper store when counter changes
    useEffect(() => {
        setQuantityStepper(counterValue);
    }, [counterValue, setQuantityStepper]);

    // Listen to stepper changes from store and sync back to counter
    // Also clear gift selections when quantity changes
    useObserve(() => {
        const quantityStepper = quantityStepper$.get() ?? 1;
        console.log('[CartCounterButton] quantityStepper changed:', quantityStepper);

        // Don't remove this condition -> it's make sure legend state return primary data
        setCounterValue(typeof quantityStepper === 'number' ? quantityStepper : 1);
        if (quantityStepper) {
            clearAttachedCartItemReferenceLogics();
        }
    });

    const emitValueChange = useCallback((count: number) => {
        setCounterValue(count);
    }, []);

    if (!showIf && !sortedGiftsDataList.length) {
        return null;
    }

    return (
        <div className={styles.widgetContainer}>
            <CartCounterButtonWidget
                variant={variant}
                value={counterValue}
                handleIncrease={handleIncrease}
                handleDecrease={handleDecrease}
                emitValueChange={emitValueChange}
            />
        </div>
    );
});

export default CartCounterButton;
